import { BASE_OPTION_PADDING } from "./consts";
import { Option } from "./types";
import { isArray, isEmpty, isString } from "lodash-es";

/**
 * Gets all leaf values (deepest level options) from an option and its children
 * @param option - The option to get leaf values from
 * @returns Array of all leaf values
 */
export function getLeafValues(option: Option): string[] {
  if (!option.children || option.children.length === 0) {
    return [option.value];
  }
  return option.children.flatMap(child => getLeafValues(child));
}

/**
 * Gets all descendant values including intermediate parents and leaves
 * @param option - The option to get descendant values from
 * @returns Array of all descendant values (including the option itself)
 */
export function getAllDescendantValues(option: Option): string[] {
  const descendants = [option.value];
  if (option.children) {
    option.children.forEach(child => {
      descendants.push(...getAllDescendantValues(child));
    });
  }
  return descendants;
}

/**
 * Checks if all children of an option are selected
 * @param option - The parent option
 * @param selected - Set of selected values
 * @returns True if all children are selected
 */
export function areAllChildrenSelected(
  option: Option,
  selected: Set<string>,
): boolean {
  if (isEmpty(option.children)) return false;
  const leafValues = getLeafValues(option);
  return leafValues.every(val => selected.has(val));
}

/**
 * Checks if any children of an option are selected
 * @param option - The parent option
 * @param selected - Set of selected values
 * @returns True if any children are selected
 */
export function areSomeChildrenSelected(
  option: Option,
  selected: Set<string>,
): boolean {
  if (isEmpty(option.children)) return false;
  const leafValues = getLeafValues(option);
  return leafValues.some(val => selected.has(val));
}

/**
 * Finds an option by its value, searching recursively through the option tree
 * @param options - The array of options to search
 * @param value - The value to find
 * @returns The found option or null
 */
export function findOptionByValue(
  options: Option[],
  value: string,
): Option | null {
  for (const option of options) {
    if (option.value === value) return option;
    if (option.children) {
      const found = findOptionByValue(option.children, value);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Normalizes value input to always return an array of strings
 * @param value - The value to normalize (string, array, or undefined)
 * @returns Array of string values
 */
export function normalizeValue(value: string | string[] | undefined): string[] {
  if (isArray(value)) return value;
  if (isString(value)) return [value];
  return [];
}

/**
 * Builds a map from options by applying an extractor function
 * @param options - The options to process
 * @param extractor - Function to extract key-value pairs from each option
 * @returns A map of the extracted values
 */
export function buildOptionMap<T>(
  options: Option[],
  extractor: (option: Option) => [string, T] | null,
): Map<string, T> {
  const map = new Map<string, T>();

  for (const option of options) {
    const entry = extractor(option);
    if (entry) map.set(entry[0], entry[1]);
    if (option.children) {
      const childMap = buildOptionMap(option.children, extractor);
      childMap.forEach((value, key) => map.set(key, value));
    }
  }

  return map;
}

/**
 * Updates parent selection states based on their children's selection status
 * Adds parent to selection if all children are selected
 * Removes parent from selection if any or no children are selected
 * @param options - The options tree to process
 * @param selectedSet - The set of selected values (modified in place)
 */
export function updateParentStates(
  options: Option[],
  selectedSet: Set<string>,
): void {
  options.forEach(option => {
    if (!isEmpty(option.children)) {
      const allSelected = areAllChildrenSelected(option, selectedSet);

      if (allSelected && !option.disabled) {
        selectedSet.add(option.value);
      } else if (!option.showIndeterminateCheckbox) {
        // Remove parent if not all children are selected (some or none), or parent is disabled
        selectedSet.delete(option.value);
      }

      if (option.children) {
        updateParentStates(option.children, selectedSet);
      }
    }
  });
}

/* Helper to get selected values as array for Set operations */
export const getSelectedValuesArray = (
  selectedValue: string[] | string | undefined,
): string[] => {
  if (!selectedValue) return [];

  if (Array.isArray(selectedValue)) return selectedValue;

  return [selectedValue];
};

/* Helper map for labels by value */
export const getLabelByValue = (options: Option[]) =>
  buildOptionMap(options, option => [option.value, option.label]);

/* Helper map for icons by value */
export const getIconByValue = (options: Option[]) =>
  buildOptionMap(options, option =>
    option.icon ? [option.value, option.icon] : null,
  );

/** * Unselects all descendant options of a given option
 * @param option - The parent option
 * @param selectedSet - The set of selected values (modified in place)
 */
export function unselectAllDescendantsOptions(
  option: Option,
  selectedSet: Set<string>,
): void {
  const allDescendants = getAllDescendantValues(option);
  allDescendants.forEach(value => selectedSet.delete(value));
}

export function selectAllDescendantsOptions(
  option: Option,
  selectedSet: Set<string>,
): void {
  const allDescendants = getAllDescendantValues(option);
  allDescendants.forEach(value => selectedSet.add(value));
}

/**
 * Filters selected values to show only parents when all their children are selected
 * @param options - The options tree to process
 * @param selected - Array of selected values
 * @returns Array of display values (parents when all children selected, individual children otherwise)
 */
export function getDisplayValues(
  options: Option[],
  selected: string[],
): string[] {
  const selectedSet = new Set(selected);

  return options.flatMap(option => {
    if (!isEmpty(option.children) && option.children) {
      return areAllChildrenSelected(option, selectedSet)
        ? [option.value]
        : getDisplayValues(option.children, selected);
    }

    return selectedSet.has(option.value) ? [option.value] : [];
  });
}

/** Calculates left padding size based on nesting level
 * @param nestLevel - The nesting level of the option
 * @returns The left padding size in pixels
 */
export function getLeftPaddingSize(nestLevel: number | undefined): number {
  if (nestLevel && nestLevel >= 1) {
    return nestLevel * BASE_OPTION_PADDING;
  }

  return 0;
}

export function getFlatSelectableOptions(options: Option[]): Option[] {
  return options.flatMap(option => {
    if (option.disabled) return [];
    const children =
      option.children && option.children.length > 0
        ? getFlatSelectableOptions(option.children)
        : [];
    return [option, ...children];
  });
}
